import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UploadItemTypeEnum } from '@/Domain/enum/UploadItemType.enum';
import { UploadStatusEnum } from '@/Domain/enum/UploadStatus.enum';
import { SCENARIO_ID } from '@/context';

export type ScrapingProgress = {
  visitedItems: number;
  totalItems: number;
  currentUrl: string;
  // True when the backend stamped capped_at_limit on the terminal
  // META_ARTICLE_DELETED / DONE event because the scrape stopped because
  // saved_count hit the scenario's articles_limit. StatusMessage renders
  // an upgrade CTA inside the success pill in that case.
  cappedAtLimit?: boolean;
};

export type UploadFile = {
  id: number;
  name: string;
  size: number;
  status: UploadStatusEnum;
  type: UploadItemTypeEnum;
  url?: string;
  scrapingProgress?: ScrapingProgress;
  errorMessage?: string;
};

export type ArticleToFileMap = Record<number, number>;

type State = {
  files: UploadFile[];
  isMinimized: boolean;
  isOpen: boolean;
  articleToFileMap: ArticleToFileMap;
};

type Actions = {
  addItems: (items: File | string | (File | string)[], type: UploadItemTypeEnum) => void;
  removeFile: (id: number) => void;
  updateItemStatus: (id: number, status: UploadStatusEnum, errorMessage?: string) => void;
  updateScrapingProgress: (id: number, progress: Partial<ScrapingProgress>) => void;
  toggleMinimize: () => void;
  setIsOpen: (open: boolean) => void;
  resetStore: () => void;
  addArticleMapping: (articleId: number, fileId: number) => void;
  getItemIdByArticleId: (articleId: number) => number | undefined;
  getUnmappedPendingFiles: () => UploadFile[];
};

export type UploadStore = State & Actions;

const defaultState: State = {
  files: [],
  isMinimized: false,
  isOpen: false,
  articleToFileMap: {},
};

export const useUploadStore = create<UploadStore>()(
  persist(
    (set, get) => ({
      ...defaultState,

      addItems: (items, type) => {
        const itemsArray = Array.isArray(items) ? items : [items];

        const newItems = itemsArray.map((item, index) => ({
          id: Date.now() + index,
          name: type === UploadItemTypeEnum.FILE ? (item as File).name : (item as string),
          size: type === UploadItemTypeEnum.FILE ? (item as File).size : 0,
          status: UploadStatusEnum.PENDING,
          type,
          ...(type === UploadItemTypeEnum.URL && { url: item as string }),
        }));

        set({
          files: [...get().files, ...newItems],
          isOpen: true,
        });
      },

      removeFile: (id: number) =>
        set({
          files: get().files.filter((file) => file.id !== id),
        }),

      updateItemStatus: (id: number, status: UploadStatusEnum, errorMessage?: string) =>
        set({
          files: get().files.map((file) =>
            file.id === id ? { ...file, status, errorMessage } : file,
          ),
        }),

      updateScrapingProgress: (id: number, progress: Partial<ScrapingProgress>) =>
        set({
          files: get().files.map((file) => {
            if (file.id !== id) return file;
            const existing = file.scrapingProgress;
            // Concurrent Firecrawl webhook handlers each emit their own
            // in-memory `saved_urls` length (see comment in
            // backend/appquickchat/api/firecrawl/router.py:_handle_page_event).
            // Under a burst this can deliver an event whose visitedItems is
            // briefly lower than what we already received from a sibling
            // handler. Drop the stale count fields so the user never sees
            // the count tick backwards mid-scrape, but keep flag fields like
            // cappedAtLimit which travel on the same event.
            const next = { ...progress };
            if (
              existing &&
              next.visitedItems !== undefined &&
              next.visitedItems < existing.visitedItems
            ) {
              delete next.visitedItems;
              delete next.totalItems;
              delete next.currentUrl;
            }
            if (Object.keys(next).length === 0) return file;
            return {
              ...file,
              // Merge so a terminal event that carries `cappedAtLimit`
              // but no counts doesn't clobber the streaming progress.
              scrapingProgress: {
                visitedItems: 0,
                totalItems: 0,
                currentUrl: '',
                ...existing,
                ...next,
              },
            };
          }),
        }),

      toggleMinimize: () => set({ isMinimized: !get().isMinimized }),

      setIsOpen: (open: boolean) => set({ isOpen: open }),

      resetStore: () => set(defaultState),

      addArticleMapping: (articleId: number, fileId: number) =>
        set({
          articleToFileMap: {
            ...get().articleToFileMap,
            [articleId]: fileId,
          },
        }),

      getItemIdByArticleId: (articleId: number) => get().articleToFileMap[articleId],

      getUnmappedPendingFiles: () => {
        const { files, articleToFileMap } = get();
        const mappedFileIds = Object.values(articleToFileMap);

        return files.filter(
          (file) =>
            [
              UploadStatusEnum.PENDING,
              UploadStatusEnum.PARSING,
              UploadStatusEnum.PARSING_FALLBACK,
            ].includes(file.status) && !mappedFileIds.includes(file.id),
        );
      },
    }),
    {
      name: `upload-store-${SCENARIO_ID}`,
      partialize: (state) => ({
        files: state.files.filter(
          (f) =>
            ![
              UploadStatusEnum.PENDING,
              UploadStatusEnum.PARSING,
              UploadStatusEnum.PARSING_FALLBACK,
            ].includes(f.status),
        ),
        isMinimized: state.isMinimized,
        isOpen: state.isOpen,
        articleToFileMap: state.articleToFileMap,
      }),
    },
  ),
);
